import { heureLocale } from "./flux-cron.js";
import { FLUX_OPERATEURS_UNAIRES, type FluxCondition } from "./flux.js";

/**
 * LES REFERENCES D'UN FLUX : `{{chemin}}` dans une valeur d'entree.
 *
 * Le contexte d'une execution est un objet a racines fixes :
 *   - `trigger` : la sortie du declencheur (la fiche, le corps du webhook...) ;
 *   - `step_N`  : la sortie de l'etape N (`{{step_1.totalMinutes}}`), et dans
 *                 une boucle `{{step_N.item}}` / `{{step_N.index}}` (1 d'abord) ;
 *   - `vars`    : les variables de l'org (`{{vars.cle}}`), secretes comprises,
 *                 resolues par le serveur seul ;
 *   - `date`    : les dates utiles, dans le fuseau du flux (`variablesDeDate`) ;
 *   - `flux`    : le flux et son execution (`{{flux.nom}}`, `{{flux.executionId}}`).
 *
 * Une valeur qui n'est QU'UNE reference garde son type ; un texte qui en
 * contient plusieurs est interpole (objets en JSON). Une reference absente
 * vaut `undefined` seule, et le texte vide dans une interpolation : le moteur
 * ne leve pas d'erreur, l'etape suivante voit ce qui manque.
 *
 * Pas d'evaluation de code : un chemin, rien d'autre. C'est la difference
 * voulue avec Activepieces, qui evalue du JavaScript dans un bac a sable.
 */

export type FluxContexte = Record<string, unknown>;

const SEUL = /^\s*\{\{\s*([^{}]+?)\s*\}\}\s*$/;
const DANS = /\{\{\s*([^{}]+?)\s*\}\}/g;

/** Decoupe `a.b[0]['c d'].e` en segments. */
export function segmentsDuChemin(chemin: string): string[] {
  const segments: string[] = [];
  const re = /([^.[\]]+)|\[\s*(\d+)\s*\]|\[\s*'([^']*)'\s*\]|\[\s*"([^"]*)"\s*\]/g;
  for (const m of chemin.matchAll(re)) {
    const v = m[1] ?? m[2] ?? m[3] ?? m[4];
    if (v !== undefined && v.trim() !== "") segments.push(v.trim());
  }
  return segments;
}

/** Lit un chemin dans le contexte ; `undefined` si un maillon manque. */
export function lireChemin(contexte: FluxContexte, chemin: string): unknown {
  let courant: unknown = contexte;
  for (const segment of segmentsDuChemin(chemin)) {
    if (courant === null || courant === undefined) return undefined;
    if (Array.isArray(courant) && /^\d+$/.test(segment)) courant = courant[Number(segment)];
    else if (typeof courant === "object") courant = (courant as Record<string, unknown>)[segment];
    else return undefined;
  }
  return courant;
}

function enTexte(valeur: unknown): string {
  if (valeur === undefined || valeur === null) return "";
  if (typeof valeur === "string") return valeur;
  if (valeur instanceof Date) return valeur.toISOString();
  // Une liste de valeurs simples se lit « alpha, beta, gamma » dans un texte ;
  // une liste d'objets, ou un objet, reste en JSON.
  if (Array.isArray(valeur) && valeur.every((v) => v === null || ["string", "number", "boolean"].includes(typeof v))) return valeur.map((v) => enTexte(v)).join(", ");
  if (typeof valeur === "object") return JSON.stringify(valeur);
  return String(valeur);
}

/** Resout toutes les references d'une valeur (texte, liste, objet), en profondeur. */
export function resoudre(valeur: unknown, contexte: FluxContexte): unknown {
  if (typeof valeur === "string") {
    const seul = SEUL.exec(valeur);
    if (seul?.[1]) return lireChemin(contexte, seul[1]);
    if (!valeur.includes("{{")) return valeur;
    return valeur.replace(DANS, (_m, chemin: string) => enTexte(lireChemin(contexte, chemin)));
  }
  if (Array.isArray(valeur)) return valeur.map((v) => resoudre(v, contexte));
  if (valeur !== null && typeof valeur === "object" && !(valeur instanceof Date)) {
    return Object.fromEntries(Object.entries(valeur).map(([k, v]) => [k, resoudre(v, contexte)]));
  }
  return valeur;
}

function nombre(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.trim() !== "") return Number(v);
  return NaN;
}
function instant(v: unknown): number {
  if (v instanceof Date) return v.getTime();
  if (typeof v === "string" || typeof v === "number") return new Date(v).getTime();
  return NaN;
}
function vrai(v: unknown): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return ["true", "vrai", "oui", "1"].includes(v.trim().toLowerCase());
  if (typeof v === "number") return v !== 0;
  return false;
}
function liste(v: unknown): unknown[] {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim().startsWith("[")) {
    try {
      const lu: unknown = JSON.parse(v);
      return Array.isArray(lu) ? lu : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Evalue une condition, ses deux cotes resolus dans le contexte. */
export function evaluerConditionFlux(condition: FluxCondition, contexte: FluxContexte): boolean {
  const g = resoudre(condition.gauche, contexte);
  const d = FLUX_OPERATEURS_UNAIRES.includes(condition.operateur) ? undefined : resoudre(condition.droite ?? "", contexte);
  const gt = enTexte(g).toLowerCase();
  const dt = enTexte(d).toLowerCase();
  switch (condition.operateur) {
    case "texte_contient": return gt.includes(dt);
    case "texte_ne_contient_pas": return !gt.includes(dt);
    case "texte_egal": return gt === dt;
    case "texte_different": return gt !== dt;
    case "texte_commence_par": return gt.startsWith(dt);
    case "texte_finit_par": return gt.endsWith(dt);
    case "nombre_superieur": return nombre(g) > nombre(d);
    case "nombre_inferieur": return nombre(g) < nombre(d);
    case "nombre_egal": return nombre(g) === nombre(d);
    case "booleen_vrai": return vrai(g);
    case "booleen_faux": return !vrai(g);
    case "existe": return g !== undefined && g !== null && g !== "";
    case "n_existe_pas": return g === undefined || g === null || g === "";
    case "liste_contient": return liste(g).some((e) => enTexte(e).toLowerCase() === dt);
    case "liste_vide": return liste(g).length === 0;
    case "liste_non_vide": return liste(g).length > 0;
    case "date_avant": return instant(g) < instant(d);
    case "date_apres": return instant(g) > instant(d);
    default: return false;
  }
}

/** Groupes en OU, conditions en ET dans chaque groupe. */
export function evaluerGroupesFlux(groupes: readonly (readonly FluxCondition[])[], contexte: FluxContexte): boolean {
  return groupes.some((groupe) => groupe.length > 0 && groupe.every((c) => evaluerConditionFlux(c, contexte)));
}

/** L'instant UTC d'une heure locale donnee dans un fuseau. */
export function instantLocal(annee: number, mois: number, jour: number, heure: number, minute: number, fuseau: string): Date {
  const cible = Date.UTC(annee, mois - 1, jour, heure, minute);
  let t = cible;
  for (let i = 0; i < 3; i += 1) {
    const l = heureLocale(new Date(t), fuseau);
    const vu = Date.UTC(l.annee, l.mois - 1, l.jour, l.heure, l.minute);
    if (vu === cible) break;
    t -= vu - cible;
  }
  return new Date(t);
}

const deux = (n: number): string => String(n).padStart(2, "0");

/**
 * Les dates de `{{date.*}}`, dans le fuseau du flux. Les `debut*` sont des
 * instants ISO (00:00 locale), les autres des jours `AAAA-MM-JJ`.
 */
export function variablesDeDate(maintenant: Date, fuseau: string): Record<string, string> {
  const l = heureLocale(maintenant, fuseau);
  const jourDecale = (decalage: number): { a: number; m: number; j: number } => {
    const d = new Date(Date.UTC(l.annee, l.mois - 1, l.jour + decalage));
    return { a: d.getUTCFullYear(), m: d.getUTCMonth() + 1, j: d.getUTCDate() };
  };
  const jour = (x: { a: number; m: number; j: number }): string => `${x.a}-${deux(x.m)}-${deux(x.j)}`;
  const debut = (x: { a: number; m: number; j: number }): string => instantLocal(x.a, x.m, x.j, 0, 0, fuseau).toISOString();
  const auj = jourDecale(0);
  const hier = jourDecale(-1);
  const demain = jourDecale(1);
  const lundi = jourDecale(-((l.jourSemaine + 6) % 7));
  const premier = { a: l.annee, m: l.mois, j: 1 };
  return {
    maintenant: maintenant.toISOString(),
    heure: `${deux(l.heure)}:${deux(l.minute)}`,
    aujourdhui: jour(auj),
    hier: jour(hier),
    demain: jour(demain),
    debutAujourdhui: debut(auj),
    debutHier: debut(hier),
    debutDemain: debut(demain),
    debutSemaine: debut(lundi),
    debutMois: debut(premier),
    fuseau,
  };
}

/** Les cles de `{{date.*}}`, pour le selecteur de donnees du builder. */
export const FLUX_CLES_DATE = ["maintenant", "heure", "aujourdhui", "hier", "demain", "debutAujourdhui", "debutHier", "debutDemain", "debutSemaine", "debutMois", "fuseau"] as const;
